/** Pass rules from spec §6 and the v1 tenant API (PassResource, Pass.php constants). */
import { endOfMoscowDay, parseServerDate } from './moscowTime';
import { normalizePlate } from './plate';

export { parseServerDate };

export const PassStatus = {
  Pending: 10,
  WaitingGuest: 20,
  InviteCanceled: 25,
  Expected: 30,
  Inside: 40,
  Expired: 50,
  Closed: 60,
} as const;

export const GuestType = { Vehicle: 1, Pedestrian: 2 } as const;

export interface AddressRef {
  id: number;
  name: string;
}

export interface GuestData {
  plateNumber?: string;
  name?: string;
}

export interface Pass {
  id: number;
  status: number;
  durationType: number;
  guestType: number | null;
  guestData: GuestData | null;
  address: AddressRef;
  /** "Y-m-d H:i:s" in server time. */
  startsAt: string | null;
  expiresAt: string | null;
  title: string;
}

export interface Template {
  id: number;
  name: string;
  title: string;
  guestType: number | null;
  durationType: number;
  guestData: GuestData | null;
  address: AddressRef;
}

const PLATE = /^([А-ЯA-Z])(\d{3})([А-ЯA-Z]{2})(\d{2,3})$/u;

/** "А123ВС777" -> number "А 123 ВС", region "777"; other formats unchanged. */
export function splitPlate(plate: string): { number: string; region: string | null } {
  const m = PLATE.exec(plate.replace(/\s/g, '').toUpperCase());
  return m ? { number: `${m[1]} ${m[2]} ${m[3]}`, region: m[4] } : { number: plate, region: null };
}

/** "Сегодня" on the home screen: guests inside, then those expected today including invites. */
export function todayPasses(passes: readonly Pass[], now: Date): Pass[] {
  const endOfDay = new Date(endOfMoscowDay(now).getTime() + 59_999);
  const startOf = (p: Pass) => parseServerDate(p.startsAt)?.getTime() ?? 0;

  return passes
    .filter((p) => {
      if (p.status === PassStatus.Inside) return true;
      if (p.status !== PassStatus.Expected && p.status !== PassStatus.WaitingGuest) return false;
      const starts = parseServerDate(p.startsAt);
      const expires = parseServerDate(p.expiresAt);
      return (!starts || starts <= endOfDay) && (!expires || expires >= now);
    })
    .sort((a, b) => Number(b.status === PassStatus.Inside) - Number(a.status === PassStatus.Inside) || startOf(a) - startOf(b));
}

export interface FrequentGuest {
  key: string;
  label: string;
  subtitle: string;
  guestType: number | null;
  plate: string | null;
  source: { kind: 'template'; id: number } | { kind: 'pass'; id: number };
}

const MAX_FREQUENT = 8;

function guestKey(guestType: number | null, data: GuestData | null): string | null {
  if (guestType === GuestType.Vehicle && data?.plateNumber) return `vehicle:${data.plateNumber}`;
  if (guestType === GuestType.Pedestrian && data?.name) return `pedestrian:${data.name}`;
  return null;
}

function subtitle(guestType: number | null, address: AddressRef): string {
  return `${guestType === GuestType.Pedestrian ? 'Пешком' : 'Машина'} · ${address.name}`;
}

const NO_MODEL = /^не задан[аоы]?$/iu;

/**
 * The server titles a car pass "<plate> — <model>", and a car without a model gets the model «Не задана».
 * Next to a drawn plate only the model is worth showing: "А787АА771 — Audi" -> "Audi", "А787АА771 — Не задана" -> "".
 * Any other title (a name the resident gave) is kept as is.
 */
export function titleBesidePlate(title: string, plate: string | null | undefined): string {
  const t = title.trim();
  if (!plate) return t;
  const [head, ...rest] = t.split(/\s+[—–-]\s+/u);
  if (normalizePlate(head) !== normalizePlate(plate)) return t;
  const model = rest.join(' — ').trim();
  return NO_MODEL.test(model) ? '' : model;
}

function guestLabel(title: string, guestType: number | null, data: GuestData | null): string {
  if (title) return titleBesidePlate(title, data?.plateNumber);
  if (guestType === GuestType.Vehicle && data?.plateNumber) {
    const { number, region } = splitPlate(data.plateNumber);
    return region ? `${number} ${region}` : number;
  }
  return data?.name ?? 'Гость';
}

const NUMBERED_TEMPLATE = /^шаблон\s*№?\s*\d*$/iu;

/**
 * What a saved template is called on screen. The server numbers unnamed templates («Шаблон 1»), which tells the
 * resident nothing (#4216): show the guest instead — the model beside a plate (the plate itself is drawn next to
 * it, so a car without a model gets no text), or the pedestrian's name.
 */
export function templateLabel(t: Pick<Template, 'name' | 'title' | 'guestType' | 'guestData'>): string {
  const name = t.name?.trim() ?? '';
  if (name && !NUMBERED_TEMPLATE.test(name)) return name;
  const title = (t.title ?? '').trim();
  if (title && !NUMBERED_TEMPLATE.test(title)) return titleBesidePlate(title, t.guestData?.plateNumber);
  if (t.guestType !== GuestType.Vehicle || !t.guestData?.plateNumber) return t.guestData?.name || 'Гость';
  return '';
}

/** Saved templates first, then recent unique guests from passes (spec §6.2). */
export function frequentGuests(templates: readonly Template[], recentPasses: readonly Pass[]): FrequentGuest[] {
  const seen = new Set<string>();
  const result: FrequentGuest[] = [];

  for (const t of templates) {
    const key = guestKey(t.guestType, t.guestData);
    if (key) seen.add(key);
    result.push({
      key: `template:${t.id}`,
      label: templateLabel(t),
      subtitle: subtitle(t.guestType, t.address),
      guestType: t.guestType,
      plate: t.guestData?.plateNumber ?? null,
      source: { kind: 'template', id: t.id },
    });
  }
  for (const p of recentPasses) {
    const key = guestKey(p.guestType, p.guestData);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    result.push({
      key,
      label: guestLabel(p.title, p.guestType, p.guestData),
      subtitle: subtitle(p.guestType, p.address),
      guestType: p.guestType,
      plate: p.guestData?.plateNumber ?? null,
      source: { kind: 'pass', id: p.id },
    });
  }
  return result.slice(0, MAX_FREQUENT);
}
